import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { memo } from "react";
import type { CommissionRateResponseDTO } from "../types/commission-rate.types";
import { RATE_TYPE_LABELS } from "./CommissionRateFormDialog";

interface CommissionRateDeleteDialogProps {
  open: boolean;
  loading: boolean;
  commissionRate: CommissionRateResponseDTO | null;
  onClose: () => void;
  onConfirm: () => void;
}

const deriveName = (cr: CommissionRateResponseDTO): string => {
  const chargeLabel = cr.rate !== null ? "Percentual" : "Fixa";
  const typeLabel = RATE_TYPE_LABELS[cr.type] ?? cr.type;
  return `Comissão ${chargeLabel} ${typeLabel}`;
};

const formatValue = (cr: CommissionRateResponseDTO): string => {
  if (cr.rate !== null) {
    const pct = cr.rate * 100;
    return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`;
  }
  if (cr.value !== null) {
    return cr.value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }
  return "-";
};

export const CommissionRateDeleteDialog = memo(
  function CommissionRateDeleteDialog({
    open,
    loading,
    commissionRate,
    onClose,
    onConfirm,
  }: CommissionRateDeleteDialogProps) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir Taxa de Comissão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir esta taxa de comissão?
            </DialogDescription>
          </DialogHeader>

          {commissionRate && (
            <div className="py-2">
              <div className="p-4 bg-destructive/10 rounded-md border border-destructive/20">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {commissionRate.name ?? deriveName(commissionRate)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Tipo:{" "}
                      <span className="font-medium">
                        {RATE_TYPE_LABELS[commissionRate.type] ??
                          commissionRate.type}
                      </span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Valor:{" "}
                      <span className="font-medium">
                        {formatValue(commissionRate)}
                      </span>
                    </p>
                    <p className="text-sm text-destructive mt-2 font-medium">
                      Esta ação não pode ser desfeita!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? "Excluindo..." : "Excluir Taxa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);
